import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createFacility,
  clearFacilityState,
} from "../redux/slices/facilitySlice";
import {
  getDashboardUsers,
  clearDashboardErrors,
} from "../redux/slices/dashboardSlice";
import Toast from "../components/Toast";

const CreateFacilityModal = () => {
  const dispatch = useDispatch();

  // Facility form state
  const [facilityName, setFacilityName] = useState("");
  const [location, setLocation] = useState("");
  const [budgetedHours, setBudgetedHours] = useState("");
  const [budgetedHoursType, setBudgetedHoursType] = useState("Weekly");

  // Customer selection (single)
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const customerDropdownRef = useRef(null);
  const customerInputRef = useRef(null);

  // Team selection (multiple)
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const teamDropdownRef = useRef(null);
  const teamInputRef = useRef(null);

  // Budgeted hours type options
  const budgetedHoursOptions = ["Weekly", "Monthly", "Quarterly", "Yearly"];

  // Get dashboard users data
  const { users, isLoading: isLoadingUsers } = useSelector(
    (state) => state.dashboard,
  );

  // Get facility creation state
  const { isCreating, error, successMessage } = useSelector(
    (state) => state.facility,
  );

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Separate users by role
  const { customers, teams } = useMemo(() => {
    if (!users || !Array.isArray(users)) {
      return { customers: [], teams: [] };
    }

    return {
      customers: users.filter((user) => user.role === "customer"),
      teams: users.filter((user) => user.role === "team"),
    };
  }, [users]);

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) {
      return customers;
    }
    const query = customerSearchQuery.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(query) ||
        (customer.user_group_name &&
          customer.user_group_name.toLowerCase().includes(query)),
    );
  }, [customers, customerSearchQuery]);

  // Filter teams based on search query
  const filteredTeams = useMemo(() => {
    if (!teamSearchQuery.trim()) {
      return teams;
    }
    const query = teamSearchQuery.toLowerCase();
    return teams.filter(
      (team) =>
        team.name.toLowerCase().includes(query) ||
        (team.role && team.role.toLowerCase().includes(query)),
    );
  }, [teams, teamSearchQuery]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "success" });
  };

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target)
      ) {
        setIsCustomerDropdownOpen(false);
        if (selectedCustomer) {
          const customer = customers.find((c) => c.id === selectedCustomer);
          setCustomerSearchQuery(customer ? customer.name : "");
        } else {
          setCustomerSearchQuery("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedCustomer, customers]);

  // Close team dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target)
      ) {
        setIsTeamDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(getDashboardUsers());
    return () => dispatch(clearDashboardErrors());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      showToast(successMessage, "success");
      // Reset form
      setFacilityName("");
      setLocation("");
      setBudgetedHours("");
      setBudgetedHoursType("Weekly");
      setSelectedCustomer("");
      setSelectedTeams([]);
      setIsCustomerDropdownOpen(false);
      setIsTeamDropdownOpen(false);
      setCustomerSearchQuery("");
      setTeamSearchQuery("");
      dispatch(clearFacilityState());
      dispatch(getDashboardUsers());
    }

    if (error) {
      showToast(error, "error");
      dispatch(clearFacilityState());
    }
  }, [successMessage, error, dispatch]);

  // Update customer search query when selected customer changes
  useEffect(() => {
    if (selectedCustomer) {
      const customer = customers.find((c) => c.id === selectedCustomer);
      setCustomerSearchQuery(customer ? customer.name : "");
    }
  }, [selectedCustomer, customers]);

  const handleSubmit = () => {
    if (!facilityName.trim()) {
      showToast("Facility name is required", "error");
      return;
    }

    if (!location.trim()) {
      showToast("Location is required", "error");
      return;
    }

    if (!budgetedHours) {
      showToast("Budgeted hours are required", "error");
      return;
    }

    if (isNaN(budgetedHours) || Number(budgetedHours) <= 0) {
      showToast("Budgeted hours must be a positive number", "error");
      return;
    }

    if (!budgetedHoursType.trim()) {
      showToast("Budgeted hours type is required", "error");
      return;
    }

    // Combine selected users: customer (single) + teams (multiple)
    const assignToUserIds = [];

    if (selectedCustomer) {
      assignToUserIds.push(selectedCustomer);
    }

    if (selectedTeams.length > 0) {
      assignToUserIds.push(...selectedTeams);
    }

    const facilityData = {
      name: facilityName,
      address: location,
      assign_to_user_id: assignToUserIds,
      budgeted_hours: Number(budgetedHours),
      budgeted_hours_type: budgetedHoursType,
    };

    dispatch(createFacility(facilityData));
  };

  // Customer dropdown handlers
  const handleCustomerInputClick = () => {
    if (!isCreating && !isLoadingUsers) {
      setIsCustomerDropdownOpen(true);
      if (selectedCustomer) {
        const customer = customers.find((c) => c.id === selectedCustomer);
        setCustomerSearchQuery(customer ? customer.name : "");
      }
      setTimeout(() => {
        if (customerInputRef.current) {
          customerInputRef.current.focus();
          customerInputRef.current.select();
        }
      }, 10);
    }
  };

  const handleCustomerInputChange = (e) => {
    if (isCustomerDropdownOpen) {
      setCustomerSearchQuery(e.target.value);
      if (selectedCustomer) {
        const customer = customers.find((c) => c.id === selectedCustomer);
        if (!e.target.value.includes(customer?.name || "")) {
          setSelectedCustomer("");
        }
      }
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomer(customerId);
    setIsCustomerDropdownOpen(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer("");
    setCustomerSearchQuery("");
    if (isCustomerDropdownOpen && customerInputRef.current) {
      customerInputRef.current.focus();
    }
  };

  // Team dropdown handlers
  const handleTeamInputClick = () => {
    if (!isCreating && !isLoadingUsers) {
      setIsTeamDropdownOpen(true);
      setTimeout(() => {
        if (teamInputRef.current) {
          teamInputRef.current.focus();
        }
      }, 10);
    }
  };

  const handleTeamInputChange = (e) => {
    if (isTeamDropdownOpen) {
      setTeamSearchQuery(e.target.value);
    }
  };

  const handleToggleTeam = (teamId) => {
    setSelectedTeams((prev) => {
      if (prev.includes(teamId)) {
        return prev.filter((id) => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleClearTeams = () => {
    setSelectedTeams([]);
    setTeamSearchQuery("");
  };

  // Get selected team names for display
  const getSelectedTeamNames = () => {
    const selectedNames = selectedTeams
      .map((id) => teams.find((t) => t.id === id)?.name)
      .filter(Boolean);

    if (selectedNames.length <= 2) {
      return selectedNames.join(", ");
    }
    return `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2} more`;
  };

  return (
    <div
      className="announcementModal"
      style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
      {/* Toast Component */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      <div
        style={{
          overflowY: "auto",
          paddingRight: "5px",
          flex: 1,
          minHeight: 0, // Important for flex children to scroll properly
        }}>
        <div className="row">
          {/* Facility Name and Location in same row */}
          <div className="row mb-20">
            <div className="col-md-6">
              <label className="fw-bold mb-2">Facility Name *</label>
              <input
                type="text"
                className="form-control"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                disabled={isCreating}
                placeholder="Enter facility name"
              />
            </div>
            <div className="col-md-6">
              <label className="fw-bold mb-2">Location *</label>
              <input
                type="text"
                className="form-control"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={isCreating}
                placeholder="Enter facility location"
              />
            </div>
          </div>

          {/* Budgeted Hours in same row */}
          <div className="row mb-20">
            <div className="col-md-6">
              <label className="fw-bold mb-2">Budgeted Hours *</label>
              <input
                type="number"
                className="form-control"
                value={budgetedHours}
                onChange={(e) => setBudgetedHours(e.target.value)}
                disabled={isCreating}
                placeholder="Enter hours"
                min="0"
                step="0.5"
              />
            </div>
            <div className="col-md-6">
              <label className="fw-bold mb-2">Budgeted Hours Type *</label>
              <input
                type="text"
                className="form-control"
                list="budgetedHoursOptions"
                placeholder="Select type"
                value={budgetedHoursType}
                onChange={(e) => setBudgetedHoursType(e.target.value)}
                disabled={isCreating}
              />
              <datalist id="budgetedHoursOptions">
                {budgetedHoursOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Customer Selection (Single) */}
          <div
            className="form-group mb-20 position-relative"
            ref={customerDropdownRef}>
            <label className="fw-bold mb-2">Assign Customer (Optional)</label>

            <div className="position-relative">
              <input
                ref={customerInputRef}
                type="text"
                className="form-control"
                value={customerSearchQuery}
                placeholder={
                  isCustomerDropdownOpen
                    ? "Search customers..."
                    : "Click to search/select customer"
                }
                onChange={handleCustomerInputChange}
                onClick={handleCustomerInputClick}
                disabled={isCreating || isLoadingUsers}
                style={{ cursor: "pointer", paddingRight: "30px" }}
              />
              {selectedCustomer && (
                <button
                  type="button"
                  className="btn btn-sm position-absolute"
                  onClick={handleClearCustomer}
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    zIndex: 1,
                    padding: "0",
                    fontSize: "18px",
                    lineHeight: "1",
                  }}>
                  ×
                </button>
              )}
            </div>

            {/* Customer Dropdown */}
            {isCustomerDropdownOpen && !isCreating && !isLoadingUsers && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1060,
                  width: "100%",
                  maxHeight: "250px",
                  overflowY: "auto",
                  top: "100%",
                  left: 0,
                  marginTop: "4px",
                }}>
                {filteredCustomers.length === 0 ? (
                  <div className="text-center py-3 small text-muted">
                    {customerSearchQuery
                      ? "No customers found"
                      : "Start typing to search customers"}
                  </div>
                ) : (
                  filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className="px-3 py-2 border-bottom"
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedCustomer === customer.id
                            ? "#e7f1ff"
                            : "transparent",
                      }}
                      onClick={() => handleSelectCustomer(customer.id)}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div style={{ flex: 1 }}>
                          <div className="fw-medium">{customer.name}</div>
                          <div className="text-muted small">
                            {customer.user_group_name && (
                              <span className="badge bg-info me-1">
                                Group: {customer.user_group_name}
                              </span>
                            )}
                            <span>Role: {customer.role}</span>
                          </div>
                        </div>
                        {selectedCustomer === customer.id && (
                          <i className="fas fa-check text-primary ms-2"></i>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Team Selection (Multiple) */}
          <div
            className="form-group mb-20 position-relative"
            ref={teamDropdownRef}>
            <label className="fw-bold mb-2">
              Assign Team Members (Optional - Multiple)
            </label>

            {/* Selected Teams Display - Compact */}
            {selectedTeams.length > 0 && (
              <div
                className="mb-2 d-flex flex-wrap gap-1"
                style={{ maxHeight: "60px", overflowY: "auto" }}>
                {selectedTeams.map((teamId) => {
                  const team = teams.find((t) => t.id === teamId);
                  return team ? (
                    <span
                      key={teamId}
                      className="badge bg-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                      style={{ fontSize: "12px" }}>
                      {team.name}
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{
                          fontSize: "8px",
                          marginLeft: "4px",
                          width: "12px",
                          height: "12px",
                        }}
                        onClick={() => handleToggleTeam(teamId)}
                        aria-label="Remove"
                      />
                    </span>
                  ) : null;
                })}
              </div>
            )}

            <div className="position-relative">
              <input
                ref={teamInputRef}
                type="text"
                className="form-control"
                value={teamSearchQuery}
                placeholder={
                  isTeamDropdownOpen
                    ? "Search team members..."
                    : selectedTeams.length > 0
                      ? `Selected: ${getSelectedTeamNames()}`
                      : "Click to search/select team members"
                }
                onChange={handleTeamInputChange}
                onClick={handleTeamInputClick}
                disabled={isCreating || isLoadingUsers}
                style={{ cursor: "pointer", paddingRight: "70px" }}
              />
              {selectedTeams.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm position-absolute"
                  onClick={handleClearTeams}
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    zIndex: 1,
                    fontSize: "12px",
                    color: "#dc3545",
                  }}>
                  Clear all
                </button>
              )}
            </div>

            {/* Team Dropdown */}
            {isTeamDropdownOpen && !isCreating && !isLoadingUsers && (
              <div
                className="position-absolute bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1060,
                  width: "100%",
                  maxHeight: "250px",
                  overflowY: "auto",
                  top: "auto",
                  bottom: "auto",
                  left: 0,
                  marginTop: "4px",
                }}>
                {filteredTeams.length === 0 ? (
                  <div className="text-center py-3 small text-muted">
                    {teamSearchQuery
                      ? "No team members found"
                      : "Start typing to search team members"}
                  </div>
                ) : (
                  filteredTeams.map((team) => (
                    <div
                      key={team.id}
                      className="px-3 py-2 border-bottom"
                      style={{
                        cursor: "pointer",
                        backgroundColor: selectedTeams.includes(team.id)
                          ? "#e7f1ff"
                          : "transparent",
                      }}
                      onClick={() => handleToggleTeam(team.id)}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div style={{ flex: 1 }}>
                          <div className="fw-medium">{team.name}</div>
                          <div className="text-muted small">
                            Role: {team.role}
                            {team.facility_name && ` • ${team.facility_name}`}
                          </div>
                        </div>
                        {selectedTeams.includes(team.id) && (
                          <i className="fas fa-check text-primary ms-2"></i>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button - Sticky at bottom */}
      <div
        className="position-sticky"
        style={{
          bottom: 0,
          backgroundColor: "white",
          paddingTop: "15px",
          paddingBottom: "10px",
          marginTop: "10px",
          borderTop: "1px solid #e0e0e0",
          zIndex: 1050,
        }}>
        <button
          className="btn announceButton facilityButton w-100"
          onClick={handleSubmit}
          disabled={isCreating}>
          {isCreating ? "Creating..." : "Add Facility"}
        </button>
      </div>
    </div>
  );
};

export default CreateFacilityModal;
